import type { VisualOutput } from "@cafe/shared";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatMetric, metricLabel } from "../../lib/metrics";
import { cn } from "../../lib/utils";
import { Card } from "../ui/primitives";
import { SourceList } from "./SourceList";

const COLORS = ["#c8873a", "#4a3326", "#7c9a6d", "#9b6bb0"];
const compact = (v: number) => new Intl.NumberFormat("es-MX", { notation: "compact", maximumFractionDigits: 1 }).format(v);

/** Renders the visual the AGENT chose (chart / table / KPIs) from server-side dataset rows. */
export function Visual({ v }: { v: VisualOutput }) {
  return (
    <Card className="p-3">
      <p className="mb-1 text-sm font-medium">{v.title}</p>
      {v.visual === "chart" && <ChartView v={v} />}
      {v.visual === "table" && <TableView v={v} />}
      {v.visual === "kpis" && <KpiView v={v} />}
      {v.caption && <p className="mt-1 text-[11px] text-roast/70">{v.caption}</p>}
      <SourceList sources={v.sources} notes={v.notes} period={v.period} />
    </Card>
  );
}

function ChartView({ v }: { v: VisualOutput }) {
  const x = v.x!;
  const ys = v.y ?? [];
  const rows = v.rows.map((r) => ({ ...r, [x]: String(r[x]) }));
  const horizontal = v.chart_type === "bar_horizontal";
  const height = horizontal ? Math.max(160, rows.length * 28) : 200;
  const fmt = (val: unknown, key: string) => formatMetric(key, val);
  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer>
        {v.chart_type === "line" ? (
          <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#e9dccd" vertical={false} />
            <XAxis dataKey={x} tick={{ fontSize: 10, fill: "#4a3326" }} tickLine={false} axisLine={false} minTickGap={12} />
            <YAxis tick={{ fontSize: 10, fill: "#4a3326" }} tickLine={false} axisLine={false} width={52} tickFormatter={compact} />
            <Tooltip formatter={(val, name) => [fmt(val, ys.length === 1 ? ys[0]! : String(name)), metricLabel(String(name))]} contentStyle={{ borderRadius: 12, borderColor: "#e9dccd", fontSize: 12 }} />
            {ys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} formatter={(n) => metricLabel(String(n))} />}
            {ys.map((y, i) => (
              <Line key={y} type="monotone" dataKey={y} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={rows.length <= 12} />
            ))}
          </LineChart>
        ) : (
          <BarChart data={rows} layout={horizontal ? "vertical" : "horizontal"} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#e9dccd" vertical={horizontal} horizontal={!horizontal} />
            {horizontal ? (
              <>
                <XAxis type="number" tick={{ fontSize: 10, fill: "#4a3326" }} tickLine={false} axisLine={false} tickFormatter={compact} />
                <YAxis type="category" dataKey={x} tick={{ fontSize: 10, fill: "#4a3326" }} tickLine={false} axisLine={false} width={110} />
              </>
            ) : (
              <>
                <XAxis dataKey={x} tick={{ fontSize: 10, fill: "#4a3326" }} tickLine={false} axisLine={false} interval={0} angle={rows.length > 7 ? -30 : 0} textAnchor={rows.length > 7 ? "end" : "middle"} height={rows.length > 7 ? 50 : 24} />
                <YAxis tick={{ fontSize: 10, fill: "#4a3326" }} tickLine={false} axisLine={false} width={52} tickFormatter={compact} />
              </>
            )}
            <Tooltip formatter={(val, name) => [fmt(val, String(name)), metricLabel(String(name))]} cursor={{ fill: "#f7f1ea" }} contentStyle={{ borderRadius: 12, borderColor: "#e9dccd", fontSize: 12 }} />
            {ys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} formatter={(n) => metricLabel(String(n))} />}
            {ys.map((y, i) => (
              <Bar key={y} dataKey={y} fill={COLORS[i % COLORS.length]} radius={horizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]}>
                {ys.length === 1 &&
                  rows.map((r) => <Cell key={String(r[x])} fill={v.highlight && String(r[x]) === v.highlight ? "#a3361f" : COLORS[0]} />)}
              </Bar>
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

function TableView({ v }: { v: VisualOutput }) {
  const cols = v.columns ?? [];
  return (
    <div className="max-h-80 overflow-auto">
      <table className="w-full text-left text-xs tabular-nums">
        <thead className="sticky top-0 bg-foam">
          <tr className="border-b border-latte">
            {cols.map((c, i) => (
              <th key={c} className={cn("py-1 pr-2 font-medium", i > 0 && "text-right")}>
                {metricLabel(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {v.rows.map((r, ri) => (
            <tr key={ri} className={cn("border-b border-latte/50", v.highlight && String(r[cols[0]!]) === v.highlight && "bg-amber-50 font-medium")}>
              {cols.map((c, i) => (
                <td key={c} className={cn("py-1 pr-2", i > 0 && "text-right")}>
                  {formatMetric(c, r[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KpiView({ v }: { v: VisualOutput }) {
  const [cur, prev] = v.rows;
  const label = v.x!;
  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {(v.y ?? []).map((m) => {
          const a = Number(cur?.[m] ?? 0);
          const b = prev ? Number(prev[m] ?? 0) : null;
          const d = b ? Math.round(((a - b) / b) * 1000) / 10 : null;
          return (
            <div key={m} className="rounded-xl bg-crema px-3 py-2">
              <p className="text-[11px] text-roast/80">{metricLabel(m)}</p>
              <p className="text-base font-semibold tabular-nums">{formatMetric(m, cur?.[m])}</p>
              {d !== null && (
                <p className={cn("flex items-center gap-0.5 text-[11px] tabular-nums", d >= 0 ? "text-emerald-700" : "text-red-700")}>
                  {d >= 0 ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
                  {d > 0 ? "+" : ""}
                  {d.toFixed(1)}% vs. {formatMetric(m, prev?.[m])}
                </p>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-roast/70">
        {String(cur?.[label] ?? "")}
        {prev ? ` vs. ${String(prev[label])}` : ""}
      </p>
    </>
  );
}
